import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { FileText, Download, Eye, Clock, HardDrive, FolderArchive, Search, X, Terminal, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../utils/api';

const Vault = () => {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewContent, setPreviewContent] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const response = await api.get('/vault/files');
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Error fetching vault files:', error);
      toast.error('Failed to load vault files');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const response = await api.get(`/vault/download/${filename}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handlePreview = async (filename) => {
    setPreviewFile(filename);
    setPreviewLoading(true);
    try {
      const response = await api.get(`/vault/preview/${filename}`);
      setPreviewContent(response.data.content);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load file preview');
      setPreviewContent('Error loading file content');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewFile(null);
    setPreviewContent('');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredFiles = files.filter(file =>
    file.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Categorize files
  const docFiles = filteredFiles.filter(f => f.filename.startsWith('DOC-'));
  const specFiles = filteredFiles.filter(f => !f.filename.startsWith('DOC-'));

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading vault...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vault-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FolderArchive className="h-6 w-6 text-amber-500" />
            Documentation Vault
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Permanent record of requirements, architecture, and decisions. Files are append-only and never deleted.
          </p>
        </div>
        
        {/* Search */}
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="vault-search"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <FileText className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{files.length}</p>
                <p className="text-xs text-muted-foreground">Total Documents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <HardDrive className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {formatFileSize(files.reduce((sum, f) => sum + f.size_bytes, 0))}
                </p>
                <p className="text-xs text-muted-foreground">Total Size</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <Clock className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{docFiles.length}</p>
                <p className="text-xs text-muted-foreground">Core DOC Files</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Core Documentation Section */}
      {docFiles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              Core Documentation (DOC-01, DOC-02)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {docFiles.map((file) => (
                <FileRow
                  key={file.filename}
                  file={file}
                  onDownload={handleDownload}
                  onPreview={handlePreview}
                  formatFileSize={formatFileSize}
                  formatDate={formatDate}
                  highlight
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Specification Documents Section */}
      {specFiles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              Specification Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {specFiles.map((file) => (
                <FileRow
                  key={file.filename}
                  file={file}
                  onDownload={handleDownload}
                  onPreview={handlePreview}
                  formatFileSize={formatFileSize}
                  formatDate={formatDate}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {filteredFiles.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">
              {searchTerm ? 'No files match your search' : 'No vault files available'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closePreview}>
          <div 
            className="bg-background rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {previewFile}
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(previewFile)}
                  data-testid="preview-download-btn"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closePreview}
                  data-testid="preview-close-btn"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-4">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-pulse text-muted-foreground">Loading preview...</div>
                </div>
              ) : (
                <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed">
                  {previewContent}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* System Logs shortcut — admin only */}
      {user?.is_admin && (
        <div className="pt-2 border-t border-border/50">
          <SystemLogsCard />
        </div>
      )}
    </div>
  );
};

// File row component
const FileRow = ({ file, onDownload, onPreview, formatFileSize, formatDate, highlight }) => (
  <div 
    className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg border ${
      highlight ? 'bg-amber-500/5 border-amber-500/20' : 'bg-muted/30'
    }`}
    data-testid={`vault-file-${file.filename}`}
  >
    <div className="flex items-start gap-3">
      <FileText className={`h-5 w-5 mt-0.5 ${highlight ? 'text-amber-500' : 'text-blue-500'}`} />
      <div>
        <p className="font-medium text-sm">{file.filename}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{file.title}</p>
        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
          <span>{formatFileSize(file.size_bytes)}</span>
          <span>•</span>
          <span>Modified: {formatDate(file.modified_at)}</span>
        </div>
      </div>
    </div>
    
    <div className="flex items-center gap-2 sm:ml-auto">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPreview(file.filename)}
        data-testid={`preview-${file.filename}`}
      >
        <Eye className="h-4 w-4 sm:mr-1" />
        <span className="hidden sm:inline">Preview</span>
      </Button>
      <Button
        variant="default"
        size="sm"
        onClick={() => onDownload(file.filename)}
        data-testid={`download-${file.filename}`}
      >
        <Download className="h-4 w-4 sm:mr-1" />
        <span className="hidden sm:inline">Download</span>
      </Button>
    </div>
  </div>
);

// ── System Logs shortcut at the bottom of the Vault ─────────────────────────
const SystemLogsCard = () => {
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get('/logs/critical-alerts').then(r => setUnread(r.data?.unread_count || 0)).catch(() => {});
  }, []);

  return (
    <Link to="/admin/logs" data-testid="vault-system-logs-link">
      <Card className="hover:bg-accent/30 transition-colors cursor-pointer border-dashed">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-500/10">
                <Terminal className="h-5 w-5 text-gray-400" />
              </div>
              <div>
                <p className="font-semibold text-sm">System Logs</p>
                <p className="text-xs text-muted-foreground">Production events, errors & performance</p>
              </div>
            </div>
            {unread > 0 && (
              <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-500/15 text-red-400 text-xs font-bold">
                <AlertTriangle size={12} />
                {unread} critical
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default Vault;
